import type { ReactNode } from 'react';
import { Panel } from '../whoop-panel';
import { TrajectoryChart } from '../whoop-charts';
import { C } from '../whoop-theme';

const DIAGNOSIS_STYLE: Record<string, { color: string; label: string }> = {
  ahead: { color: C.good, label: 'Ahead' },
  ontrack: { color: C.accent, label: 'On track' },
  behind: { color: C.warn, label: 'Behind' },
};

const DIAGNOSIS = [
  {
    label: 'Consistency',
    status: 'ahead',
    note: '5 of 5 runs completed last 3 weeks. Your strongest lever right now.',
  },
  {
    label: 'Weekly volume',
    status: 'behind',
    note: '38 mi vs. ~46 mi needed at this stage for the goal pace.',
  },
  {
    label: 'Long-run development',
    status: 'ontrack',
    note: 'Progressing +1 mi/wk, on the standard curve toward 20 mi.',
  },
  {
    label: 'Threshold exposure',
    status: 'behind',
    note: 'Quality sessions cut short twice by recovery — closing slower than ideal.',
  },
  {
    label: 'Easy-run base',
    status: 'ahead',
    note: 'Plenty of aerobic volume at the right easy intensity.',
  },
  {
    label: 'Recovery stability',
    status: 'ontrack',
    note: 'Mostly steady; two dips after high-strain stacks.',
  },
];

const LEVERS = [
  {
    n: '1',
    title: 'Add one 30-min easy run per week',
    benefit: 'Raises aerobic volume toward the goal range with minimal added fatigue.',
    delta: '+aerobic base',
  },
  {
    n: '2',
    title: 'Protect one threshold session every 7–10 days',
    benefit: 'Closes the gap between current pace and goal marathon pace.',
    delta: '+race pace',
  },
  {
    n: '3',
    title: 'Stop stacking hard days within 48 h',
    benefit: 'Improves quality-session completion, which is the main thing limiting progress.',
    delta: '+completion',
  },
];

const ESTIMATES = [
  { dist: '5K', range: '23:30–24:10' },
  { dist: '10K', range: '49:00–50:30' },
  { dist: 'Half', range: '1:48–1:52' },
  { dist: 'Marathon', range: '3:49–3:57' },
];

const SCENARIOS = [
  { label: 'Current trajectory', pct: 41, color: C.warn },
  { label: '+ one easy run / week', pct: 55, color: C.accent },
  { label: '+ easy run & steady threshold', pct: 64, color: C.good },
];

const CONF_NOTE =
  'WHOOP-only estimate — built from workout duration, frequency, strain and recovery, not exact pace. Connect Strava or Garmin to fold in splits, HR zones and elevation for a high-confidence projection.';

export function ProgressPage() {
  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: '-0.025em', margin: 0 }}>
          Progress
        </h1>
        <div style={{ fontSize: 14, color: C.text2, marginTop: 4 }}>
          Where I am today versus where I need to be — and what&apos;s driving the gap.
        </div>
      </div>

      {/* Current vs target */}
      <Panel
        style={{
          padding: '22px 24px',
          marginBottom: 18,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '24px 38px',
        }}
      >
        <StatBlock label="Goal" value="3:45:00" sub="8:34 / mi" mono />
        <Divider />
        <StatBlock
          label="Current estimate"
          value="3:49–3:57"
          valueColor={C.accent}
          sub="gap ≈ 4–12 min"
          mono
        />
        <Divider />
        <StatBlock label="Time remaining" value="15 weeks" />
        <Divider />
        <StatBlock label="Confidence" value="Moderate" valueColor={C.warn} />
      </Panel>

      {/* Trajectory */}
      <Panel style={{ padding: '22px 24px', marginBottom: 18 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 6,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div style={{ fontSize: 14, fontWeight: 700 }}>Predicted marathon finish</div>
          <div
            style={{
              display: 'flex',
              gap: 16,
              fontSize: '11.5px',
              color: C.text2,
              fontFamily: "'JetBrains Mono',monospace",
            }}
          >
            <LegendItem
              swatch={
                <span
                  style={{ width: 14, height: 2, background: C.text2, display: 'inline-block' }}
                />
              }
              label="actual"
            />
            <LegendItem
              swatch={
                <span
                  style={{
                    width: 14,
                    height: 0,
                    borderTop: `2px dashed ${C.accent}`,
                    display: 'inline-block',
                  }}
                />
              }
              label="projection"
            />
            <LegendItem
              swatch={
                <span
                  style={{
                    width: 14,
                    height: 2,
                    borderTop: `2px dashed ${C.good}`,
                    display: 'inline-block',
                  }}
                />
              }
              label="goal"
            />
          </div>
        </div>
        <TrajectoryChart />
        <div style={{ fontSize: '12.5px', color: C.text2, lineHeight: 1.5, marginTop: 6 }}>
          On the current curve you land around{' '}
          <span style={{ color: C.text, fontWeight: 600 }}>3:46</span> — just outside the goal,
          inside the uncertainty band. Closing the last minute depends on volume and threshold
          consistency.
        </div>
      </Panel>

      {/* Gap diagnosis */}
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Why the gap exists</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill,minmax(238px,1fr))',
          gap: 12,
          marginBottom: 24,
        }}
      >
        {DIAGNOSIS.map((d) => {
          const style = DIAGNOSIS_STYLE[d.status] ?? DIAGNOSIS_STYLE.ontrack;
          const color = style?.color ?? C.accent;
          return (
            <div
              key={d.label}
              style={{
                border: '1px solid rgba(255,255,255,0.08)',
                background: C.panel,
                borderRadius: 12,
                padding: '15px 16px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 8,
                }}
              >
                <span style={{ fontSize: 13, fontWeight: 700 }}>{d.label}</span>
                <span
                  style={{
                    fontSize: '10.5px',
                    fontFamily: "'JetBrains Mono',monospace",
                    color,
                    border: `1px solid ${color}`,
                    borderRadius: 5,
                    padding: '2px 7px',
                    opacity: 0.9,
                  }}
                >
                  {style?.label}
                </span>
              </div>
              <div style={{ fontSize: '12.5px', color: C.text2, lineHeight: 1.45 }}>{d.note}</div>
            </div>
          );
        })}
      </div>

      {/* Levers */}
      <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Highest-impact changes</div>
      <div
        style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 14, marginBottom: 24 }}
      >
        {LEVERS.map((lv) => (
          <div
            key={lv.n}
            style={{
              border: '1px solid rgba(181,155,255,0.22)',
              background: C.panelAccent,
              borderRadius: 14,
              padding: 18,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
              <span
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 7,
                  background: 'rgba(181,155,255,0.16)',
                  color: C.accent,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 800,
                  fontSize: 12,
                  fontFamily: "'JetBrains Mono',monospace",
                }}
              >
                {lv.n}
              </span>
              <span
                style={{ fontSize: 11, fontFamily: "'JetBrains Mono',monospace", color: C.accent }}
              >
                {lv.delta}
              </span>
            </div>
            <div style={{ fontSize: 14, fontWeight: 700, lineHeight: 1.3, marginBottom: 8 }}>
              {lv.title}
            </div>
            <div style={{ fontSize: '12.5px', color: C.text2, lineHeight: 1.45 }}>{lv.benefit}</div>
          </div>
        ))}
      </div>

      {/* Race pace estimator */}
      <Panel style={{ padding: '22px 24px' }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 4 }}>Race pace estimator</div>
        <div style={{ fontSize: '12.5px', color: C.text2, marginBottom: 20 }}>
          Distance equivalents from recent training, with goal probability.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.1fr', gap: 30 }}>
          <div>
            <div
              style={{
                fontSize: 11,
                fontFamily: "'JetBrains Mono',monospace",
                letterSpacing: '0.1em',
                color: C.text3,
                textTransform: 'uppercase',
                marginBottom: 8,
              }}
            >
              Estimated ranges
            </div>
            {ESTIMATES.map((e) => (
              <div
                key={e.dist}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '11px 0',
                  borderTop: '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <span style={{ fontSize: 14, fontWeight: 600 }}>{e.dist}</span>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    fontFamily: "'JetBrains Mono',monospace",
                  }}
                >
                  {e.range}
                </span>
              </div>
            ))}
          </div>
          <div>
            <div
              style={{
                fontSize: 11,
                fontFamily: "'JetBrains Mono',monospace",
                letterSpacing: '0.1em',
                color: C.text3,
                textTransform: 'uppercase',
                marginBottom: 14,
              }}
            >
              Sub-3:45 probability by race day
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
              {SCENARIOS.map((s) => (
                <div key={s.label}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'baseline',
                      marginBottom: 6,
                    }}
                  >
                    <span style={{ fontSize: 13, color: '#cbc9c2' }}>{s.label}</span>
                    <span
                      style={{
                        fontSize: 15,
                        fontWeight: 800,
                        fontFamily: "'JetBrains Mono',monospace",
                        color: s.color,
                      }}
                    >
                      {s.pct}%
                    </span>
                  </div>
                  <div
                    style={{
                      height: 8,
                      borderRadius: 5,
                      background: 'rgba(255,255,255,0.06)',
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${s.pct}%`,
                        background: s.color,
                        borderRadius: 5,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 10,
            alignItems: 'flex-start',
            fontSize: 12,
            color: C.text3,
            lineHeight: 1.5,
            marginTop: 20,
            paddingTop: 16,
            borderTop: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <span style={{ color: C.warn, fontWeight: 700 }}>ⓘ</span>
          {CONF_NOTE}
        </div>
      </Panel>
    </div>
  );
}

function StatBlock({
  label,
  value,
  sub,
  valueColor,
  mono = false,
}: {
  label: string;
  value: string;
  sub?: string;
  valueColor?: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div style={{ fontSize: 11, color: C.text3, marginBottom: 5 }}>{label}</div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          fontFamily: mono ? "'JetBrains Mono',monospace" : undefined,
          color: valueColor,
        }}
      >
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: C.text2, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, background: 'rgba(255,255,255,0.08)' }} />;
}

function LegendItem({ swatch, label }: { swatch: ReactNode; label: string }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {swatch}
      {label}
    </span>
  );
}
